import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { createGallery, deleteGallery, getGallery } from "../../redux/gallery/gallerySlice";
import { getEventLocations } from "../../redux/eventLocation/eventLocationSlice";
import { getImageUrlHelper } from "../../helper/helper";
import Select from "react-select";
import { processFileUpload, FILE_TYPES } from "../../utils/fileUploadHelper";
import PaginationHelper from "../../helper/PaginationHelper";

const GalleryLayer = () => {

  const dispatch = useDispatch();
  const BASE_URL = process.env.REACT_APP_API_URL;

  const { gallery = [], total = 0 } = useSelector((state) => state.gallery);
  const { locations = [] } = useSelector((state) => state.eventLocation);

  const [entries, setEntries] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);

  const [formData, setFormData] = useState({
    eventLocation: null,
    images: [],
  });

  const [previewImages, setPreviewImages] = useState([]);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    dispatch(getGallery({ page: currentPage, limit: entries }));
  }, [dispatch, currentPage, entries]);

  const totalPages = Math.ceil(total / entries);
  const startIndex = (currentPage - 1) * entries;
  const endIndex = startIndex + entries;

  const locationOptions = locations.map((l) => ({
    label: l.name,
    value: l.id,
  }));

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);

    const processedFiles = [];

    for (let file of files) {
      const processed = await processFileUpload(file, {
        allowedTypes: FILE_TYPES.IMAGE,
      });

      if (processed) {
        processedFiles.push(processed);
      }
    }

    if (!processedFiles.length) return;

    setFormData({
      ...formData,
      images: processedFiles,
    });

    const previews = processedFiles.map((file) =>
      URL.createObjectURL(file)
    );

    setPreviewImages(previews);
  };

  const handleSubmit = () => {
    if (!formData.eventLocation) {
      toast.error("Location is required");
      return;
    }

    if (!formData.images || formData.images.length === 0) {
      toast.error("At least one image required");
      return;
    }

    const fd = new FormData();
    fd.append("eventLocationId", formData.eventLocation.value);

    formData.images.forEach((img) => {
      fd.append("images", img);
    });

    dispatch(createGallery(fd))
      .unwrap()
      .then((res) => {
        toast.success(res?.message || "Created successfully");

        dispatch(getGallery({ page: currentPage, limit: entries }));

        setPreviewImages([]);
        setFormData({ eventLocation: null, images: [] });

        document.querySelector("#galleryModal .btn-close")?.click();
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong"
        );
      });
  };

  return (
    <>
      <div className="card h-100 p-0 radius-12">

        {/* HEADER */}
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between">

          <div className="d-flex gap-3">
            <select
              className="form-select form-select-sm"
              value={entries}
              onChange={(e) => {
                setEntries(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="150">150</option>
              <option value="200">200</option>
            </select>
          </div>

          <button
            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            data-bs-toggle="modal"
            data-bs-target="#galleryModal"
            onClick={() => {
              dispatch(getEventLocations());
            }}
          >
            <Icon icon="ic:baseline-plus" /> Add Gallery
          </button>

        </div>

        {/* TABLE */}
        <div className="card-body p-12">


          <div
            className="d-grid gap-16"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))"
            }}
          >

            {gallery.map((g) => (

              <div key={g.id} className="gallery-card">

                {/* IMAGE WRAPPER */}
                <div
                  className="gallery-image-wrapper"
                  onClick={() => setSelectedImage(g)}
                  data-bs-toggle="modal"
                  data-bs-target="#previewModal"
                >
                  <img
                    src={getImageUrlHelper(g.path)}
                    className="gallery-image"
                  />

                  {/* HOVER OVERLAY */}
                  <div className="gallery-overlay">
                    <Icon icon="mdi:eye-outline" />
                  </div>
                </div>

                {/* DELETE BUTTON */}
                <button
                  className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle gallery-delete-btn"
                  data-bs-toggle="modal"
                  data-bs-target="#deleteModal"
                  onClick={() => setDeleteId(g.id)}
                >
                  <Icon icon="fluent:delete-24-regular" />
                </button>

                {/* LOCATION */}
                <p className="gallery-title">
                  {g?.event?.name || "N/A"}
                </p>

              </div>

            ))}

          </div>

          <div className="modal fade" id="previewModal">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">

                {/* HEADER WITH CLOSE ICON */}
                <div className="modal-header border-0 pb-0">
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>

                {/* BODY */}
                <div className="modal-body text-center pt-0">

                  {selectedImage && (
                    <>
                      <img
                        src={getImageUrlHelper(selectedImage.path)}
                        className="w-100 radius-12"
                        style={{
                          maxHeight: "500px",
                          objectFit: "contain"
                        }}
                      />

                      <p className="mt-2 fw-medium">
                        {selectedImage?.event?.name}
                      </p>
                    </>
                  )}

                </div>

              </div>
            </div>
          </div>

          {/* PAGINATION */}
          <PaginationHelper
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={total}
            pageSize={entries}
          />

        </div>
      </div>

      {/* CREATE MODAL */}
      <div className="modal fade" id="galleryModal">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">

            <div className="modal-header">
              <h5>Add Gallery</h5>
              <button className="btn-close" data-bs-dismiss="modal" />
            </div>

            <div className="modal-body">

              <div className="mb-3">
                <label>
                  Location <span className="text-danger">*</span>
                </label>

                <Select
                  options={locationOptions}
                  value={formData.eventLocation}
                  onChange={(val) =>
                    setFormData({ ...formData, eventLocation: val })
                  }
                  placeholder="Select Location"
                />
              </div>

              <div className="mb-3">
                <label>
                  Upload Images <span className="text-danger">*</span>
                </label>

                <label
                  htmlFor="galleryUpload"
                  className="border radius-8 border-dashed d-flex align-items-center justify-content-center"
                  style={{ height: "150px", cursor: "pointer" }}
                >
                  <div className="text-center">
                    <Icon icon="solar:camera-outline" />
                    <p><small>(Only images allowed, multiple)</small></p>
                  </div>
                </label>

                <input
                  id="galleryUpload"
                  type="file"
                  hidden
                  multiple
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleImageChange}
                />
              </div>
              <div className="d-flex gap-2 flex-wrap">
                {previewImages.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    className="w-80-px h-80-px object-fit-cover radius-8"
                  />
                ))}
              </div>

              <div className="text-end mt-3">
                <button className="btn btn-primary" onClick={handleSubmit}>
                  Create
                </button>
              </div>

            </div>

          </div>
        </div>
      </div>

      {/* DELETE MODAL */}
      <div className="modal fade" id="deleteModal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">

            <div className="modal-header">
              <h5>Delete</h5>
              <button className="btn-close" data-bs-dismiss="modal" />
            </div>

            <div className="modal-body text-center">

              <p>Are you sure want to delete this gallery image?</p>

              <button
                className="btn btn-danger"
                onClick={() => {
                  dispatch(deleteGallery(deleteId))
                    .unwrap()
                    .then((res) => {
                      toast.success(res?.message || "Deleted successfully");

                      dispatch(getGallery({ page: currentPage, limit: entries }));

                      document.querySelector("#deleteModal .btn-close")?.click();
                    })
                    .catch((err) => {
                      toast.error(
                        err?.response?.data?.message ||
                        err?.message ||
                        "Delete failed"
                      );
                    });
                }}
              >
                Delete
              </button>

            </div>

          </div>
        </div>
      </div>

    </>
  );
};

export default GalleryLayer;